import React, { useState, useEffect } from 'react'
import { apiClient } from '@/utils/api'
import { Key, Loader2, AlertCircle, ShieldCheck, Tablet } from 'lucide-react'

export const KidPasswordAuthSettings: React.FC = () => {
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updating, setUpdating] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    fetchStatus()
  }, [])

  const fetchStatus = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.getKidPasswordAuthStatus()
      setEnabled(response.enabled)
    } catch (err) {
      setError('Failed to load kid password auth status')
      console.error('Error fetching kid password auth status:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = async () => {
    try {
      setUpdating(true)
      setError(null)
      setSuccess(null)
      const newValue = !enabled
      const response = await apiClient.updateKidPasswordAuth(newValue)
      setEnabled(response.enabled)
      setSuccess(newValue ? 'Kid password login enabled' : 'Kid password login disabled')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error)
      } else {
        setError('Failed to update setting')
      }
      console.error('Error updating kid password auth:', err)
    } finally {
      setUpdating(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start space-x-3">
        <div className="p-2 bg-indigo-100 rounded-lg">
          <Key className="h-6 w-6 text-indigo-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">Kid Password Login</h3>
          <p className="text-sm text-gray-600 mt-1">
            Allow children to log in with a username and password instead of Google/Apple sign-in.
            This is useful for devices without Google Play Services (like Amazon Fire tablets).
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <ShieldCheck className="h-5 w-5 text-green-500 flex-shrink-0" />
          <span className="text-sm text-green-700">{success}</span>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Tablet className="h-5 w-5 text-gray-500" />
            <div>
              <p className="font-medium text-gray-900">Enable Kid Password Login</p>
              <p className="text-sm text-gray-500">
                {enabled ? 'Kids can log in with username/password' : 'Kids must use OAuth sign-in'}
              </p>
            </div>
          </div>
          <button
            onClick={handleToggle}
            disabled={updating}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
              enabled ? 'bg-indigo-600' : 'bg-gray-200'
            } ${updating ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {enabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">How it works</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>1. Set a password for each kid in the User Management section</li>
            <li>2. Kids can then log in using your family code + their username + password</li>
            <li>3. Your family code is: <span className="font-mono font-bold">{window.location.hostname.split('.')[0]}</span></li>
            <li>4. Parents still use Google/Apple sign-in (password login is for kids only)</li>
          </ul>
        </div>
      )}

      <div className="text-xs text-gray-500 border-t pt-4">
        <p className="font-medium mb-1">Security Note</p>
        <p>
          Kid passwords are designed to be simple (minimum 4 characters) for ease of use.
          Parents can reset passwords at any time through User Management.
        </p>
      </div>
    </div>
  )
}
